import React, { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Divider
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { read, utils } from 'xlsx';
import api from '../../utils/api';

function productToExportRow(product) {
  return {
    codice: product.code,
    nome: product.name,
    descrizione: product.description || '',
    categoria: product.category,
    marca: product.brand || '',
    prezzoAcquisto: parseFloat(product.purchasePrice) || 0,
    prezzoVendita: parseFloat(product.sellingPrice) || 0,
    tempoMontaggioMin: parseFloat(product.assemblyTime) || 0,
    scorta: parseInt(product.stock, 10) || 0,
    scortaMinima: parseInt(product.minStock, 10) || 0,
    ubicazione: product.location || '',
    servizio: product.isLabor ? 'si' : 'no',
    nonRiordinare: product.doNotRestock ? 'si' : 'no',
    attivo: product.isActive !== false ? 'si' : 'no'
  };
}

function downloadWorkbook(rows, filename, sheetName) {
  const worksheet = utils.json_to_sheet(rows);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, sheetName);
  const excelBuffer = window.XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const WarehouseImportExport = ({ onComplete, disabled = false }) => {
  const importInputRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const menuOpen = Boolean(menuAnchor);
  const busy = disabled || exporting || importing;

  const closeMenu = () => setMenuAnchor(null);

  const handleExport = async () => {
    closeMenu();
    setExporting(true);
    try {
      const res = await api.get('/products', { params: { all: true, includeInactive: true } });
      const products = Array.isArray(res.data) ? res.data : (res.data?.products || []);
      if (!products.length) {
        window.alert('Nessun prodotto da esportare.');
        return;
      }
      const rows = products.map(productToExportRow);
      const date = new Date().toISOString().slice(0, 10);
      downloadWorkbook(rows, `magazzino_${date}.xlsx`, 'Magazzino');
    } catch (err) {
      console.error('Errore export magazzino:', err);
      window.alert('Errore durante l\'esportazione del magazzino.');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    closeMenu();
    try {
      const res = await api.get('/products/import/template');
      if (res.data?.success) {
        downloadWorkbook(res.data.template, 'magazzino_template.xlsx', 'Magazzino');
      }
    } catch (err) {
      console.error('Errore download template:', err);
      window.alert('Errore durante il download del template.');
    }
  };

  const handleImportClick = () => {
    closeMenu();
    importInputRef.current?.click();
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      setImporting(true);
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const rows = utils.sheet_to_json(workbook.Sheets[sheetName]);

        if (!rows.length) {
          window.alert('Il file Excel è vuoto.');
          return;
        }

        const response = await api.post('/products/import', { products: rows });
        setImportResult(response.data);
        setResultDialogOpen(true);
        if (response.data?.imported > 0 && onComplete) {
          onComplete();
        }
      } catch (err) {
        console.error('Errore import magazzino:', err);
        setImportResult({
          success: false,
          message: err.response?.data?.message || 'Errore durante l\'importazione del magazzino.',
          errors: err.response?.data?.errors || []
        });
        setResultDialogOpen(true);
      } finally {
        setImporting(false);
        if (importInputRef.current) {
          importInputRef.current.value = '';
        }
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <Tooltip title="Esporta / importa magazzino">
        <span>
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={busy}
            color="primary"
            aria-label="Esporta e importa magazzino"
            sx={{
              border: 1,
              borderColor: 'primary.main',
              borderRadius: 1,
              width: 36,
              height: 36
            }}
          >
            {busy ? <CircularProgress size={22} color="inherit" /> : <SettingsIcon />}
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleExport} disabled={busy}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          Esporta magazzino
        </MenuItem>
        <MenuItem onClick={handleDownloadTemplate} disabled={busy}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          Scarica template
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleImportClick} disabled={busy}>
          <ListItemIcon>
            <FileUploadIcon fontSize="small" />
          </ListItemIcon>
          Importa da Excel
        </MenuItem>
      </Menu>

      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleImportFile}
      />

      <Dialog open={resultDialogOpen} onClose={() => setResultDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Risultato importazione magazzino</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert severity={importResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {importResult.message}
              </Alert>
              {importResult.success && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Creati: {importResult.created || 0} · Aggiornati: {importResult.updated || 0} · Totale righe: {importResult.total || 0}
                </Typography>
              )}
              {importResult.errors?.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Errori ({importResult.errors.length}):
                  </Typography>
                  <List dense sx={{ maxHeight: 240, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                    {importResult.errors.map((error, index) => (
                      <ListItem key={index} disablePadding sx={{ px: 1 }}>
                        <ListItemText primary={error} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WarehouseImportExport;
